import backgroundBronze from '@assets/jackpot-eq/dropdown-bronze.png'
import backgroundSilver from '@assets/jackpot-eq/dropdown-silver.png'
import backgroundGold from '@assets/jackpot-eq/dropdown-gold.png'
import backgroundPlatinum from '@assets/jackpot-eq/dropdown-platinum.png'
import { cn, useShrinkTextToFit } from '@amusnet/react-ui'
import { useConfigurationStore } from '@stores/configurationStore'
import { useJackpotStore } from '@stores/jackpotStore'
import { formatDate } from '@utils/misc'
import { formatMoney } from '@utils/money'
import { JackpotEQLevels, JackpotLevelIndexMap, JackpotLevelRomanNumberMap, ViewportType } from 'game-ui-types'
import React, { useEffect, useRef, useState } from 'react'
import { JackpotEQColors } from '@components/JackpotSection/JackpotEQ/config.ts'
import { useBetSliderStore } from '@stores'
import { useTranslation } from '@amusnet/translations/react'

const backgrounds = {
    [JackpotEQLevels.BRONZE]: backgroundBronze,
    [JackpotEQLevels.SILVER]: backgroundSilver,
    [JackpotEQLevels.GOLD]: backgroundGold,
    [JackpotEQLevels.PLATINUM]: backgroundPlatinum,
}

interface JackpotEQTooltipProps {
    level: JackpotEQLevels
    isActive: boolean
}

const JackpotEQTooltip: React.FC<JackpotEQTooltipProps> = ({ level, isActive }) => {
    const { t } = useTranslation()
    const toolTipDateSeparator = useJackpotStore((state) => state.toolTipDateSeparator)
    const isRemoteGame = useConfigurationStore((state) => state.isRemoteGame)

    const currency = useConfigurationStore((state) => state.currency)
    const viewport = useConfigurationStore((state) => state.viewport)
    const currencyHideCents = useConfigurationStore((state) => state.currencyHideCents)

    const activeBetGroupBetIndex = useBetSliderStore((state) => state.activeBetGroupBetIndex)

    const [shouldShow, setShouldShow] = useState(false)

    const jackpotEQStatistics = useJackpotStore((state) => state.jackpotEQStatistics)
    const romanNumber = JackpotLevelRomanNumberMap[level]
    const levelNumber = JackpotLevelIndexMap[level]
    const remoteEQJackpotStatisticsLevels = isRemoteGame ? (jackpotEQStatistics.egyptQuest?.levels ?? null) : null
    const largestWinnerDate = formatDate(
        remoteEQJackpotStatisticsLevels
            ? (remoteEQJackpotStatisticsLevels[activeBetGroupBetIndex][levelNumber].largestWin?.winDate ?? '')
            : jackpotEQStatistics[`largestWinDateLevel${romanNumber}`],
        toolTipDateSeparator
    )
    const winAmountLargestWinner = remoteEQJackpotStatisticsLevels
        ? remoteEQJackpotStatisticsLevels![activeBetGroupBetIndex][levelNumber].largestWin?.winAmount
        : Number(jackpotEQStatistics[`largestWinLevel${romanNumber}`])
    const lastWinnerDate = formatDate(
        remoteEQJackpotStatisticsLevels
            ? (remoteEQJackpotStatisticsLevels[activeBetGroupBetIndex][levelNumber].lastWin?.winDate ?? '')
            : jackpotEQStatistics[`lastWinDateLevel${romanNumber}`],
        toolTipDateSeparator
    )
    const usernameLastWinner = remoteEQJackpotStatisticsLevels
        ? (remoteEQJackpotStatisticsLevels[activeBetGroupBetIndex][levelNumber].lastWin?.winUser ?? '')
        : jackpotEQStatistics[`lastWinUserLevel${romanNumber}`]
    const winAmountLastWinner = remoteEQJackpotStatisticsLevels
        ? remoteEQJackpotStatisticsLevels[activeBetGroupBetIndex][levelNumber].lastWin?.winAmount
        : Number(jackpotEQStatistics[`lastWinLevel${romanNumber}`])
    const numberOfWinners = remoteEQJackpotStatisticsLevels
        ? remoteEQJackpotStatisticsLevels[activeBetGroupBetIndex][levelNumber].winCount
        : jackpotEQStatistics[`winsLevel${romanNumber}`]
    const largestWinnerAmountRef = useRef<HTMLSpanElement | null>(null)
    const lastWinAmountRef = useRef<HTMLSpanElement | null>(null)
    const width = viewport === ViewportType.DESKTOP ? 305 : 330

    useEffect(() => {
        setShouldShow(isActive && numberOfWinners > 0)
    }, [isActive, jackpotEQStatistics])

    const jackpotTooltipClasses = cn(
        `jackpot-tooltip-${level.toLowerCase()}`,
        'absolute h-[400px] top-full text-[20px] leading-[24px] whitespace-break-spaces pointer-events-none z-[-1] overflow-hidden',
        `${viewport === ViewportType.DESKTOP ? 'left-1/2 -translate-x-1/2' : 'right-0'}`,
        {
            'left-0':
                (viewport === ViewportType.MOBILE_PORTRAIT &&
                    (level === JackpotEQLevels.PLATINUM || level === JackpotEQLevels.SILVER)) ||
                (viewport === ViewportType.MOBILE_LANDSCAPE && level === JackpotEQLevels.PLATINUM),
            'left-[50%] -translate-x-[50%]':
                viewport === ViewportType.MOBILE_LANDSCAPE &&
                (level === JackpotEQLevels.GOLD || level === JackpotEQLevels.SILVER),
            'pointer-events-auto': shouldShow,
            'visibility-hidden': !shouldShow,
        }
    )

    const currencyColorStyle: Record<JackpotEQLevels, string> = {
        [JackpotEQLevels.BRONZE]: JackpotEQColors.BRONZE,
        [JackpotEQLevels.SILVER]: '#B6B6B6',
        [JackpotEQLevels.GOLD]: JackpotEQColors.GOLD,
        [JackpotEQLevels.PLATINUM]: '#B6B6B6',
    }

    useShrinkTextToFit({
        refresh: [winAmountLargestWinner, shouldShow],
        designFontSize: 24,
        bounds: { width: width * 0.7 },
        textRef: largestWinnerAmountRef,
    })

    useShrinkTextToFit({
        refresh: [winAmountLastWinner, shouldShow],
        designFontSize: 24,
        bounds: { width: width * 0.7 },
        textRef: lastWinAmountRef,
    })

    const displayUsername =
        usernameLastWinner.length > 20 ? `${usernameLastWinner.substring(0, 20)}...` : usernameLastWinner

    return (
        <div
            className={jackpotTooltipClasses}
            style={{
                width,
                transition: 'visibility .3s linear, opacity .3s',
                visibility: shouldShow ? 'visible' : 'hidden',
                opacity: shouldShow ? 1 : 0,
            }}
        >
            <div
                className="relative w-full h-[348px] overflow-hidden"
                style={{
                    transition: 'transform 0.4s cubic-bezier(0.18, 0.89, 0.32, 1.28)',
                    transform: shouldShow ? 'translateY(0)' : 'translateY(-100%)',
                }}
            >
                <img
                    src={backgrounds[level]}
                    alt={`${level} Tooltip Background`}
                    className="absolute inset-0 w-full h-full object-fill"
                />

                <div className="absolute w-full h-full text-white font-bold pt-[29px] pb-[24px]">
                    {/* ✅ fixed: was `absolutetop-[24px]` (missing space) */}
                    <div className="absolute top-[24px] w-full px-[12px] flex align-start justify-between">
                        <div className="text-[#B6B6B6]">{t('gameui.largestWinner')}</div>
                        <div>{largestWinnerDate}</div>
                    </div>
                    <div className="absolute top-[95px] w-full px-[12px] text-center">
                        <span
                            ref={largestWinnerAmountRef}
                            className="font-black text-[24px] text-nowrap whitespace-nowrap"
                            style={{ color: JackpotEQColors[level] }}
                        >
                            {formatMoney(winAmountLargestWinner, currencyHideCents)}
                        </span>
                        <span className="ml-[5px]" style={{ color: currencyColorStyle[level] }}>
                            {currency}
                        </span>
                    </div>
                    <div className="absolute top-[142px] w-full px-[12px] flex align-start justify-between">
                        <div className="text-[#B6B6B6]">{t('gameui.numberOfWinners')}</div>
                        <div>{numberOfWinners}</div>
                    </div>
                    <div className="absolute top-[206px] w-full px-[12px] flex align-start justify-between">
                        <div className="text-[#B6B6B6]">{t('gameui.lastWinner')}</div>
                        <div>{lastWinnerDate}</div>
                    </div>
                    <div className="absolute top-[276px] w-full px-[12px] text-[#B6B6B6] text-center">
                        {displayUsername}
                    </div>
                    <div className="absolute top-[311px] w-full px-[12px] text-center">
                        <span ref={lastWinAmountRef} className="font-black text-[24px] text-nowrap whitespace-nowrap">
                            {formatMoney(winAmountLastWinner, currencyHideCents)}
                        </span>
                        <span className="ml-[5px] text-[#B6B6B6]">{currency}</span>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default JackpotEQTooltip
